import { useEffect, useState } from 'react';
import { Panel, Spinner, Flex, Button, Typography } from '@maxhub/max-ui';
import { useAuthStore } from './store/auth.store.ts';
import { useHousesStore } from './store/houses.store.ts';
import { register } from './api/auth.ts';
import { createPlatformBridge } from './platform/index.ts';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { RegisterPage } from './pages/RegisterPage.tsx';
import { OnboardingPage } from './pages/OnboardingPage.tsx';
import { PendingApprovalPage } from './pages/PendingApprovalPage.tsx';
import { SubmitReadingPage } from './pages/SubmitReadingPage.tsx';
import { ReadingsHistoryPage } from './pages/ReadingsHistoryPage.tsx';
import { ManageHousePage } from './pages/ManageHousePage.tsx';
import { ReceiptsPage } from './pages/ReceiptsPage.tsx';
import { ReceiptViewPage } from './pages/ReceiptViewPage.tsx';
import { AdminPage } from './pages/AdminPage.tsx';

const bridge = createPlatformBridge();

// ─── Navigation state ────────────────────────────────────────────────────────

type Screen =
  | { name: 'dashboard' }
  | { name: 'submit_reading'; meterId: string }
  | { name: 'readings_history'; meterId: string }
  | { name: 'manage_house'; houseId: string }
  | { name: 'receipts'; houseId: string }
  | { name: 'receipt_view'; receiptId: string }
  | { name: 'admin' };

export function App() {
  const { status, needsOnboarding, error, setAuth, reset, setLoading, setError, completeOnboarding } =
    useAuthStore();
  const { reset: resetHouses } = useHousesStore();
  void resetHouses; // used on logout from child screens
  const [screen, setScreen] = useState<Screen>({ name: 'dashboard' });

  // Silent re-login через initData при каждом открытии приложения —
  // не доверяем старому токену из localStorage между запусками.
  useEffect(() => {
    const platform = bridge.getPlatform();
    if (platform === 'mock') {
      reset();
      return;
    }

    setLoading();
    register({ platform, initData: bridge.getInitData() })
      .then((res) => {
        if (res.requiresPhone) {
          reset();
          return;
        }
        setAuth(res.token, res.userId, res.isApproved, res.needsOnboarding, res.role);
      })
      .catch(() => setError('Не удалось подключиться. Проверьте интернет-соединение.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auth screens ─────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <Panel centeredX centeredY style={{ minHeight: '100vh' }}>
        <Spinner size={24} />
      </Panel>
    );
  }

  if (status === 'error') {
    return (
      <Panel centeredX centeredY style={{ minHeight: '100vh', padding: '1rem' }}>
        <Flex direction="column" align="center" gap={12} style={{ textAlign: 'center' }}>
          <Typography.Label style={{ display: 'block' }}>
            {error ?? 'Не удалось подключиться.'}
          </Typography.Label>
          <Button mode="primary" onClick={() => window.location.reload()}>
            Повторить
          </Button>
        </Flex>
      </Panel>
    );
  }

  if (status === 'idle') {
    return <RegisterPage />;
  }

  if (needsOnboarding) {
    return <OnboardingPage onComplete={completeOnboarding} />;
  }

  if (status === 'pending_approval') {
    return <PendingApprovalPage />;
  }

  // ─── App screens ──────────────────────────────────────────────────────────

  if (screen.name === 'submit_reading') {
    return (
      <SubmitReadingPage
        meterId={screen.meterId}
        onBack={() => setScreen({ name: 'dashboard' })}
        onSuccess={() => setScreen({ name: 'dashboard' })}
      />
    );
  }

  if (screen.name === 'readings_history') {
    return (
      <ReadingsHistoryPage
        meterId={screen.meterId}
        onBack={() => setScreen({ name: 'dashboard' })}
      />
    );
  }

  if (screen.name === 'manage_house') {
    return (
      <ManageHousePage
        houseId={screen.houseId}
        onBack={() => setScreen({ name: 'dashboard' })}
      />
    );
  }

  if (screen.name === 'receipts') {
    return (
      <ReceiptsPage
        houseId={screen.houseId}
        onBack={() => setScreen({ name: 'dashboard' })}
        onViewReceipt={(receiptId) => setScreen({ name: 'receipt_view', receiptId })}
      />
    );
  }

  if (screen.name === 'receipt_view') {
    return (
      <ReceiptViewPage
        receiptId={screen.receiptId}
        onBack={() => setScreen({ name: 'dashboard' })}
      />
    );
  }

  if (screen.name === 'admin') {
    return <AdminPage onBack={() => setScreen({ name: 'dashboard' })} />;
  }

  return (
    <DashboardPage
      onSubmitReading={(meterId) => setScreen({ name: 'submit_reading', meterId })}
      onViewHistory={(meterId) => setScreen({ name: 'readings_history', meterId })}
      onManageHouse={(houseId) => setScreen({ name: 'manage_house', houseId })}
      onViewReceipts={(houseId) => setScreen({ name: 'receipts', houseId })}
      onOpenAdmin={() => setScreen({ name: 'admin' })}
    />
  );
}
